/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Component, useEffect } from 'react';
import { Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { getRouteTitle, nonAppRoutes, ROUTES } from '@constants/routes';
import { Loader } from '@g17eco/atoms/loader';
import { AuthRoute } from './routes/AuthRoute';
import CustomLoginCallback from './routes/CustomLoginCallback';
import { hidePermissionDenied, setAuthenticationToken, unloadAuthenticationToken } from './actions/auth';
import { loadCurrentUser } from './actions/user';
import { getUserRoles, getUserFullName } from '@utils/user';
import { CookieAcceptance } from '@features/cookie-acceptance';
import { Footer } from '@g17eco/molecules/footer';
import Header from '@features/header';
import Legal from './routes/legal/Legal';
import Onboarding from './routes/onboarding';
import Registration from './routes/registration';
import Unsubscribe from './routes/unsubscribe';
import UTrModal from '@features/utr-modal';
import UserActions from './routes/user-actions';
import { getCurrentUser } from './selectors/user';
import queryString from 'query-string';
import NotAuthorised from './routes/not-authorised';
import NotActivated from './routes/not-activated';
import BrowserCheck, { warnBrowserVersions } from './BrowserCheck';
import { RouteErrorBoundary } from '@features/error';
import { BrowserSupport } from '@g17eco/molecules/browser-support';
import { UpgradeModal } from '@features/upgrade-modal';
import { NotFound } from './routes/not-found/NotFound';
import { AppDispatch, RootState } from './reducers';
import { HydratedRouteInterface, HydratedRoutesInterface } from './types/routes';
import { appEntryRoutes } from './routes/appEntryRoutes';
import { getAnalytics } from '@services/analytics/AnalyticsService';
import { loadRootInitiatives } from './slice/rootInitiativeListSlice';
import { Security } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { RestoreOriginalUriFunction } from '@okta/okta-react/bundles/types/OktaContext';
import { OktaClient } from '@services/OktaClient';
import { onboardingRouteEntry } from './routes/company-tracker-light-onboarding';
import { SsoRoute } from './routes/login/SsoRoute';
import { getAppFromGroup } from '@utils/permission-groups';
import { SGXEsgenome } from './routes/sgx-esgenome';
import { RedirectRoute } from './routes/redirect/RedirectRoute';
import { InitiativeTags } from './types/initiative';
import './css/app.scss';
import { TriggeredModal } from './routes/home/partials/TriggeredModal';
import { setSentryUser } from './logger';
import { CustomAgreementRoute } from '@routes/legal/CustomAgreementRoute';
import { getHydratedRoutes } from './constants/routeComponents';

/**
 * Show/hide the Zendesk support bubble based on login state (GU-6692)
 * Widget is hidden by default via zESettings in index.html
 */
const ChatbotVisibility = ({ isLoggedIn }: { isLoggedIn: boolean }) => {
  useEffect(() => {
    if (window.zE) {
      window.zE('messenger', isLoggedIn ? 'show' : 'hide');
    }
  }, [isLoggedIn]);

  return null;
};

type OwnProps = RouteComponentProps<any>;

type Props = PropsFromRedux & OwnProps

class App extends Component<Props> {

  private readonly oktaAuth: OktaAuth;
  private readonly restoreOriginalUri: RestoreOriginalUriFunction;
  private readonly routes: HydratedRoutesInterface;
  private readonly nonAppComponentRoutes: HydratedRouteInterface[];

  constructor(props: Props) {
    super(props);
    this.oktaAuth = OktaClient;
    this.restoreOriginalUri = async (_oktaAuth, originalUri) => {
      props.history.replace(toRelativeUrl(originalUri || '/', window.location.origin));
    };
    this.routes = getHydratedRoutes(ROUTES);
    this.nonAppComponentRoutes = Object.values(getHydratedRoutes(nonAppRoutes)).filter(r => r.component);
  }

  mainContentContainerRef = React.createRef<HTMLDivElement>();
  analytics = getAnalytics();

  async componentDidMount() {

    if (this.props.token) {
      this.props.setAuthenticationToken(this.props.token as string);
    }

    try {
      if (await this.oktaAuth.isAuthenticated()) {
        this.props.loadRootInitiatives();
      }
    } catch (e) {
      console.error(e);
    }

    this.props.loadCurrentUser(this.props.forcedUserId as string);
    this.oktaAuth.authStateManager.subscribe((update) => {
      if (!this.props.user && update?.isAuthenticated) {
        this.props.loadCurrentUser(this.props.forcedUserId as string, true);
      }
    })

    document.title = getRouteTitle(this.props.location.pathname)
  }

  componentWillUnmount() {
    this.props.unloadAuthenticationToken();
  }

  componentDidUpdate(prevProps: Readonly<Props>) {
    const { user, location, showPermissionDenied } = this.props;

    if (user && user !== prevProps.user) {
      this.props.loadRootInitiatives();
    }
    this.initAnalytics(prevProps)

    if (location.pathname !== prevProps.location.pathname) {
      document.title = getRouteTitle(location.pathname);
      this.analytics.page()
      if (showPermissionDenied) {
        this.props.hidePermissionDenied();
      }

      // Scroll to the top on path change, but ignore search changes
      // company tracker scope/delegation/overview etc.
      setImmediate(() => {
        if (this.mainContentContainerRef.current) {
          // Its actual main container with scrollbar, not window
          this.mainContentContainerRef.current.scrollTop = 0;
        }
      });
    }
  }

  renderUnsupported = () => {
    return <div className='notification-bar always-on-top bg-warning text-dark'>
      <b><u>Deprecated Browser Warning</u></b>: For the best possible experience please
      switch to an up-to-date version of <b>Chrome</b>, <b>Firefox</b>, <b>Edge</b> or <b>Safari</b>.
    </div>
  }

  render() {
    const { loggedIn, user } = this.props;

    const showNotActivated = user && !user.active;

    const breadcrumbTitle = `${document.title}`;
    const returnUrl = encodeURIComponent(`${this.props.history.location.pathname}${this.props.history.location.search ?? ''}`);

    return (
      <Security oktaAuth={this.oktaAuth} restoreOriginalUri={this.restoreOriginalUri} >
        <ChatbotVisibility isLoggedIn={loggedIn} />
        <BrowserSupport config={warnBrowserVersions} showBoth={true} unsupportedComponent={this.renderUnsupported} />
        <Switch>
          <Route path='/login/callback' component={CustomLoginCallback} />
          <Route exact path='/login/:action(sso)?/:orgCode?' component={SsoRoute} />
          <Route path={this.routes.SGX_ESGENOME.path} component={SGXEsgenome} />
          {/* No cookie consent */}
          <Route {...this.routes.PUBLIC_SDG_CHART} />
          <Route {...this.routes.SHARED_CUSTOM_DASHBOARD} />
          <Route>
            {/* With cookie consent */}
            <Switch>
              <Route exact path='/direct/user/actions/:filter?' component={UserActions} />
              <Route exact path='/direct/registration/:stage?/:token?' component={Registration} />
              <Route exact path='/direct/onboarding/:filter?' component={Onboarding} />
              <Route exact path='/direct/unsubscribe/:token?' component={Unsubscribe} />
              <Route exact path='/agreements/:code' component={CustomAgreementRoute} />
              <Route exact path='/legal-:type?/:id?' component={Legal} />
              <Route {...onboardingRouteEntry} />

              <Route>
                <div className={`${loggedIn ? 'logged-in' : ''} root-container h-100 position-relative overflow-hidden`}>
                  {showNotActivated
                    ?
                    <NotActivated />
                    :
                    <div ref={this.mainContentContainerRef} className='main-container d-flex flex-column'>
                      {this.renderMainRoutes()}
                    </div>
                  }
                  <UpgradeModal />

                  <TriggeredModal />
                </div>
              </Route>
            </Switch>
            <CookieAcceptance returnUrl={returnUrl} breadcrumbTitle={breadcrumbTitle} />
          </Route>
        </Switch>
      </Security>
    );
  }

  renderMainRoutes() {
    const { showPermissionDenied, userError, loggedIn } = this.props;

    if (showPermissionDenied) {
      return <NotAuthorised />
    }

    // Wait until current user request is finished, error or success
    if (!userError && !loggedIn) {
      return <Loader />
    }

    // Try App routes first, the fallback to legacy routing
    return (
      <Switch>
        <Route path='/redirect' component={RedirectRoute} />
        {appEntryRoutes.map(r => this.componentRoute(r, true))}
        <Route>
          <Route path='/:root?/:unused?' component={Header} />
          <RouteErrorBoundary>
            <div className='main d-flex'>
              <Switch>
                {this.nonAppComponentRoutes.map(r => this.componentRoute(r))}
                <Route component={NotFound} />
              </Switch>
            </div>
            <UTrModal />
            <Footer />
          </RouteErrorBoundary>
        </Route>
      </Switch>
    )
  }

  componentRoute = (route: HydratedRouteInterface, showHeader = false) => {
    if (!route.component) {
      return null;
    }

    return <AuthRoute
      key={`route_${route.id}`}
      id={route.id}
      route={route}
      path={route.path}
      exact={route.exact ?? false}
      authRequired={route.auth}
      showHeader={showHeader}
    />;
  }

  private initAnalytics(prevProps: Readonly<Props>) {

    const { user, globalDataState: { organization } } = this.props;

    if (!user) {
      setSentryUser(null);
      return;
    }

    const orghasChanged = organization && organization !== prevProps.globalDataState.organization;
    const appConfig = organization?.appConfig;

    if (user !== prevProps.user || orghasChanged) {
      this.analytics.identify(user._id, {
        isStaff: user.isStaff ? '1' : '0',
        name: `${user.firstName ?? ''} ${user.surname?.[0] ?? ''}`.trim(),
        orgId: organization?._id,
        orgName: organization?.name,
        appConfigCode: organization?.appConfigCode,
        orgApp: getAppFromGroup(organization?.permissionGroup),
        productCode: appConfig?.productCode,
        orgIsStaff: organization?.tags?.includes(InitiativeTags.StaffOrganization),
        referralCode: organization?.referrals?.[0]?.code
      });

      setSentryUser({
        id: user._id,
        organization: organization ? {
          id: organization._id,
          name: organization.name,
          appConfigCode: organization.appConfigCode,
        } : undefined,
      });

      if (organization) {
        this.analytics.group(organization)
      }
    }

    const pendo = window.pendo;
    if (pendo) {
      const prevOrg = prevProps.globalDataState.organization;
      if (pendo.visitorId === user._id && pendo.accountId === organization?._id && organization === prevOrg) {
        return; // skip, nothing changed
      }

      const account = {
        id: organization?._id ?? user.permissions?.[0]?.initiativeId,
        name: organization?.name,
        appConfigCode: organization?.appConfigCode,
        productCode: appConfig?.productCode,
        sponsorCodes: organization?.sponsorships?.map(s => s.sponsorCode) ?? [],
      };
      const visitor = {
        id: user._id,
        full_name: getUserFullName(user),
        roles: getUserRoles(user),
      };

      if (user !== prevProps.user) {
        pendo.initialize({ visitor, account });
      } else if (organization !== prevOrg) {
        pendo.identify({ visitor, account });
      }
    }
  }
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const query = queryString.parse(ownProps.location.search);
  return ({
    token: query.token,
    forcedUserId: query.h,
    showPermissionDenied: state.authentication.showPermissionDenied,
    globalDataState: state.globalData,
    loggedIn: state.currentUser.loaded,
    user: getCurrentUser(state),
    userError: state.currentUser.errored,
  });
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setAuthenticationToken: (token: string) => dispatch(setAuthenticationToken(token)),
  unloadAuthenticationToken: () => dispatch(unloadAuthenticationToken()),
  loadCurrentUser: (forcedUserId?: string, refresh = false) => dispatch(loadCurrentUser(refresh, forcedUserId)),
  loadRootInitiatives: () => dispatch(loadRootInitiatives()),
  hidePermissionDenied: () => dispatch(hidePermissionDenied()),
});

const connectorFn = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connectorFn>
const connectedApp = connectorFn(App);
export default BrowserCheck(withRouter(connectedApp));
